import axios from "axios";
import { IS_LOCAL_EDITION } from "./edition";

const RAW_BACKEND_URL = String(process.env.REACT_APP_BACKEND_URL || "").trim();
const BACKEND_BASE =
  RAW_BACKEND_URL && RAW_BACKEND_URL !== "undefined" && RAW_BACKEND_URL !== "null"
    ? RAW_BACKEND_URL.replace(/\/+$/, "")
    : "";
const API = `${BACKEND_BASE}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });
const rawApi = axios.create({ baseURL: API, withCredentials: true });

function withProduct(params = {}, product = "") {
  return product ? { ...params, product } : params;
}

let refreshInFlight = null;

async function refreshAccessToken() {
  if (!refreshInFlight) {
    refreshInFlight = rawApi.post("/auth/refresh", {})
      .then((r) => {
        return r.data?.accessToken || null;
      })
      .catch((err) => {
        localStorage.removeItem("dteflow_user");
        throw err;
      })
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

// Auto-logout si el backend rechaza con 401 (excepto en login/register)
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const url = err.config?.url || "";
    const isAuthEndpoint =
      url.includes("/auth/login") ||
      url.includes("/auth/register") ||
      url.includes("/auth/google/") ||
      url.includes("/auth/refresh") ||
      url.includes("/auth/logout");
    const publicPaths = ["/", "/login", "/register", "/forgot-password", "/reset-password", "/invite"];
    const isPublicPage = publicPaths.some((p) => window.location.pathname === p || window.location.pathname.startsWith(`${p}/`));
    if (IS_LOCAL_EDITION) return Promise.reject(err);
    if (err.response?.status === 401 && !isAuthEndpoint && !err.config?._retry) {
      try {
        await refreshAccessToken();
        const nextConfig = { ...(err.config || {}), _retry: true };
        return api(nextConfig);
      } catch (_) {
        // seguir con el logout implicito debajo
      }
      const had = localStorage.getItem("dteflow_user");
      localStorage.removeItem("dteflow_user");
      if (had && !isPublicPage && !window.location.pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

export const authApi = {
  register: (email, password, name) =>
    api.post("/auth/register", { email, password, name, auth_client: "web" }).then(r => r.data),
  login: (email, password) =>
    api.post("/auth/login", { email, password, auth_client: "web" }).then(r => r.data),
  googleStart: (params = {}) =>
    api.get("/auth/google/start", { params }).then(r => r.data),
  googleExchange: (code) =>
    api.post("/auth/google/exchange", { code }).then(r => r.data),
  me: () => api.get("/auth/me").then(r => r.data),
  refresh: () =>
    api.post("/auth/refresh", {}).then(r => r.data),
  logout: () => api.post("/auth/logout").then(r => r.data),
  completeOnboarding: () => api.post("/auth/onboarding/complete").then(r => r.data),
};

export const dteApi = {
  catalogs: () => api.get("/catalogs").then(r => r.data),
  assistantAsk: (payload) => api.post("/assistant/ask", payload).then(r => r.data),
  getEmisor: () => api.get("/config/emisor").then(r => r.data),
  putEmisor: (cfg) => api.put("/config/emisor", cfg).then(r => r.data),
  patchEmisorActivity: (cfg) => api.patch("/config/emisor/actividad", cfg).then(r => r.data),
  authMH: (user, pwd, ambiente) => api.post("/auth/mh-token", { user, pwd, ambiente }).then(r => r.data),
  testMH: (ambiente) => api.post("/auth/mh-test", { ambiente }).then(r => r.data),
  currentAuth: () => api.get("/auth/current").then(r => r.data),
  firmadorStatus: () => api.get("/firmador/status").then(r => r.data),
  me: () => api.get("/auth/me").then(r => r.data),
  uploadCert: (nit, file, kind = "crt", ambiente_asignado = null) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("nit", nit);
    fd.append("kind", kind);
    if (ambiente_asignado) fd.append("ambiente_asignado", ambiente_asignado);
    return api.post("/certs/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
  },
  listCerts: () => api.get("/certs/list").then(r => r.data),
  certStatus: (nit) => api.get(`/certs/status/${nit}`).then(r => r.data),
  deleteCert: (nit, kind, ambiente = null) => {
    const params = new URLSearchParams();
    if (kind) params.set("kind", kind);
    if (ambiente) params.set("ambiente", ambiente);
    const qs = params.toString();
    return api.delete(`/certs/${nit}${qs ? `?${qs}` : ""}`).then(r => r.data);
  },
  generate: (payload) => api.post("/dte/generate", payload).then(r => r.data),
  duplicate: (documento) => api.post("/dte/duplicate", { documento }).then(r => r.data),
  signSend: (payload) => api.post("/dte/sign-send", payload).then(r => r.data),
  history: (params = {}) => api.get("/history", { params }).then(r => r.data),
  deleteHistory: (id) => api.delete(`/history/${id}`).then(r => r.data),
  progress: () => api.get("/progress").then(r => r.data),
  pdfUrl: (id, plantilla) => `${API}/dte/pdf/${id}${plantilla ? `?plantilla=${plantilla}` : ""}`,
  jsonUrl: (id) => `${API}/dte/json/${id}`,
  csvUrl: (params = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined) qs.set(k, v);
    });
    const s = qs.toString();
    return `${API}/history/export.csv${s ? `?${s}` : ""}`;
  },
  jsonHistoryUrl: () => `${API}/history/export/json`,
  // Branding (logo + plantilla por tipo + color)
  getBranding: () => api.get("/config/branding").then(r => r.data),
  putBranding: (cfg) => api.put("/config/branding", cfg).then(r => r.data),
  uploadLogo: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post("/config/branding/logo", fd, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
  },
  deleteLogo: () => api.delete("/config/branding/logo").then(r => r.data),
  logoImageUrl: () => `${API}/config/branding/logo-image?t=${Date.now()}`,
  brandingPreviewUrl: (plantilla, tipoDte = "01", bust = 0, asImage = false, overrides = {}) => {
    const params = new URLSearchParams();
    params.set("plantilla", plantilla);
    params.set("tipoDte", tipoDte);
    params.set("v", String(bust || Date.now()));
    if (asImage) params.set("as", "image");
    for (const [k, v] of Object.entries(overrides || {})) {
      if (v !== undefined && v !== null && v !== "") params.set(k, String(v));
    }
    return `${API}/config/branding/preview?${params.toString()}`;
  },
  // Modo del sistema (online/offline)
  getSystem: () => api.get("/config/system").then(r => r.data),
  putSystem: (cfg) => api.put("/config/system", cfg).then(r => r.data),
  // Procesar pendientes OFFLINE en lote
  processOfflineBatch: () => api.post("/dte/process-offline-batch").then(r => r.data),
  // Email
  getEmailConfig: () => api.get("/config/email").then(r => r.data),
  putEmailConfig: (cfg) => api.put("/config/email", cfg).then(r => r.data),
  testEmailConfig: (to = "") => api.post("/config/email/test", { to }).then(r => r.data),
  emailSyncConfig: () => api.get("/email-sync/config").then(r => r.data),
  emailSyncSave: (cfg) => api.put("/email-sync/config", cfg).then(r => r.data),
  emailSyncTest: () => api.post("/email-sync/test").then(r => r.data),
  emailSyncRun: (payload = {}) => api.post("/email-sync/run", payload, { timeout: 180000 }).then(r => r.data),
  emailSyncGoogleStart: () => api.get("/email-sync/oauth/google/start").then(r => r.data),
  emailSyncGoogleDisconnect: () => api.post("/email-sync/oauth/google/disconnect").then(r => r.data),
  emailDte: (id, body = {}) => api.post(`/dte/email/${id}`, body).then(r => r.data),
  // ── CONTABILIDAD ──
  acctChart: () => api.get("/accounting/chart").then(r => r.data),
  acctAddAccount: (a) => api.post("/accounting/chart", a).then(r => r.data),
  acctDelAccount: (codigo) => api.delete(`/accounting/chart/${codigo}`).then(r => r.data),
  acctJournal: (params = {}) => api.get("/accounting/journal", { params }).then(r => r.data),
  acctLedger: (codigo, params = {}) => api.get(`/accounting/ledger/${codigo}`, { params }).then(r => r.data),
  acctSalesBook: (params = {}) => api.get("/accounting/sales-book", { params }).then(r => r.data),
  acctPurchases: (params = {}) => api.get("/accounting/purchases", { params }).then(r => r.data),
  acctAddPurchase: (p) => api.post("/accounting/purchases", p).then(r => r.data),
  acctDelPurchase: (id) => api.delete(`/accounting/purchases/${id}`).then(r => r.data),
  acctReceivables: (params = {}) => api.get("/accounting/receivables", { params }).then(r => r.data),
  acctPayables: (params = {}) => api.get("/accounting/payables", { params }).then(r => r.data),
  acctPayReceivable: (id, body) => api.post(`/accounting/receivables/${id}/pay`, body).then(r => r.data),
  acctPayPayable: (id, body) => api.post(`/accounting/payables/${id}/pay`, body).then(r => r.data),
  acctDashboard: (params = {}) => api.get("/accounting/dashboard", { params }).then(r => r.data),
  acctBackfill: () => api.post("/accounting/backfill").then(r => r.data),
  // ── DASHBOARD EJECUTIVO ──
  executiveDashboard: (params = {}) => api.get("/dashboard/executive", { params }).then(r => r.data),
  // ── DTE RECIBIDOS (compras de proveedores) ──
  inboxParse: (file) => {
    const fd = new FormData(); fd.append("file", file);
    return api.post("/inbox/parse", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 }).then(r => r.data);
  },
  inboxImport: (file, opts = {}) => {
    const fd = new FormData(); fd.append("file", file);
    Object.entries(opts).forEach(([k, v]) => v != null && fd.append(k, v));
    return api.post("/inbox/import", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 }).then(r => r.data);
  },
  // ── STAGING (revisión humana antes de importar) ──
  inboxStage: (files) => {
    const fd = new FormData();
    const list = Array.isArray(files) ? files : [files];
    const clean = list.filter(Boolean);
    if (clean.length === 1) fd.append("file", clean[0]);
    else clean.forEach((file) => fd.append("files", file));
    return api.post("/inbox/stage", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 }).then(r => r.data);
  },
  inboxStagedList: (params = {}) => api.get("/inbox/staged", { params }).then(r => r.data),
  inboxStagedGet: (sid) => api.get(`/inbox/staged/${sid}`).then(r => r.data),
  inboxStagedPatch: (sid, body) => api.patch(`/inbox/staged/${sid}`, body).then(r => r.data),
  inboxStagedApprove: (sid, body = {}) => api.post(`/inbox/staged/${sid}/approve`, body).then(r => r.data),
  inboxStagedReject: (sid, motivo) => api.post(`/inbox/staged/${sid}/reject`, { motivo }).then(r => r.data),
  inboxStagedDelete: (sid) => api.delete(`/inbox/staged/${sid}`).then(r => r.data),
  inboxStagedReocr: (sid) => api.post(`/inbox/staged/${sid}/reocr`).then(r => r.data),
  // ── PERIODO — SNAPSHOT (iter43) ──
  periodSnapshotJsonUrl: (year, month) => `${API}/period/snapshot.json?year=${year}&month=${month}`,
  periodSnapshotZipUrl: (year, month) => `${API}/period/snapshot.zip?year=${year}&month=${month}`,
  // ── ACCOUNTING EXPORTS + TRAZABILIDAD (iter47) ──
  accJournalXlsxUrl: (params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([_, v]) => v !== "" && v !== null && v !== undefined)).toString();
    return `${API}/accounting/journal.xlsx${qs ? "?" + qs : ""}`;
  },
  accJournalPdfUrl: (params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([_, v]) => v !== "" && v !== null && v !== undefined)).toString();
    return `${API}/accounting/journal.pdf${qs ? "?" + qs : ""}`;
  },
  accLedgerXlsxUrl: (codigo, params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([_, v]) => v !== "" && v !== null && v !== undefined)).toString();
    return `${API}/accounting/ledger/${encodeURIComponent(codigo)}.xlsx${qs ? "?" + qs : ""}`;
  },
  accLedgerPdfUrl: (codigo, params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([_, v]) => v !== "" && v !== null && v !== undefined)).toString();
    return `${API}/accounting/ledger/${encodeURIComponent(codigo)}.pdf${qs ? "?" + qs : ""}`;
  },
  accTrace: (docId) => api.get(`/accounting/trace/${encodeURIComponent(docId)}`).then(r => r.data),
  inboxStagedFileUrl: (sid) => `${API}/inbox/staged/${sid}/file`,
  // Descarga el archivo original con Authorization header y devuelve un objectURL.
  // El caller debe llamar URL.revokeObjectURL al cerrar el modal.
  inboxStagedFileBlob: async (sid) => {
    const r = await api.get(`/inbox/staged/${sid}/file`, { responseType: "blob" });
    return {
      url: URL.createObjectURL(r.data),
      mediaType: String(r.headers?.["content-type"] || r.data?.type || "").toLowerCase(),
    };
  },
  inboxBackfillPeriodos: () => api.post("/inbox/backfill-periodos").then(r => r.data),
  inboxBatchApprove: (ids, forzar = false) => api.post("/inbox/staged/batch-approve", { ids, forzar }).then(r => r.data),
  // ── ROLES / USERS ──
  usersList: () => api.get("/users").then(r => r.data),
  userChangeRole: (userId, role) => api.patch(`/users/${userId}/role`, { role }).then(r => r.data),
  userInvite: (email, name, role, password) => api.post("/users/invite", { email, name, role, password }).then(r => r.data),
  userDelete: (userId) => api.delete(`/users/${encodeURIComponent(userId)}`).then(r => r.data),
  // ── Export Excel staged list ──
  inboxStagedXlsxUrl: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return `${API}/inbox/staged.xlsx${qs ? "?" + qs : ""}`;
  },
  // ── ANULACIÓN DE ASIENTOS ──
  journalAnular: (jeId, motivo) => api.post(`/accounting/journal-entries/${jeId}/anular`, { motivo }).then(r => r.data),
  // ── VALIDADOR IVA PRE-EMISIÓN ──
  acctValidatePreEmit: (documento) => api.post("/accounting/validate-pre-emit", documento).then(r => r.data),
  // ── AUDITORÍA ──
  auditList: (params = {}) => api.get("/audit", { params }).then(r => r.data),
  auditSummary: (days = 30) => api.get("/audit/summary", { params: { days } }).then(r => r.data),
  auditCsvUrl: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return `${API}/audit.csv${qs ? "?" + qs : ""}`;
  },
  // ── METADATA ──
  metaRoles: () => api.get("/meta/roles").then(r => r.data),
  // ── F-14 + ANEXOS ──
  taxF14: (params = {}) => api.get("/tax/f14", { params }).then(r => r.data),
  taxAnexoVentasUrl: (year, month) => `${API}/tax/anexo/ventas.csv?year=${year}&month=${month}`,
  taxAnexoVentasContribuyentesUrl: (year, month) => `${API}/tax/anexo/ventas-contribuyentes.csv?year=${year}&month=${month}`,
  taxAnexoVentasConsumidorFinalUrl: (year, month) => `${API}/tax/anexo/ventas-consumidor-final.csv?year=${year}&month=${month}`,
  taxAnexoComprasUrl: (year, month) => `${API}/tax/anexo/compras.csv?year=${year}&month=${month}`,
  taxAnexoSeUrl: (year, month) => `${API}/tax/anexo/sujetos-excluidos.csv?year=${year}&month=${month}`,
  // ── Exportaciones Excel ──
  libroVentasXlsxUrl: (year, month) => `${API}/tax/libro-ventas.xlsx?year=${year}&month=${month}`,
  libroComprasXlsxUrl: (year, month) => `${API}/tax/libro-compras.xlsx?year=${year}&month=${month}`,
  libroDiarioXlsxUrl: (year, month) => `${API}/tax/libro-diario.xlsx?year=${year}&month=${month}`,
  libroMayorXlsxUrl: (codigo, year, month) => {
    const qs = new URLSearchParams({ codigo });
    if (year) qs.set("year", year);
    if (month) qs.set("month", month);
    return `${API}/tax/libro-mayor.xlsx?${qs.toString()}`;
  },
  f14XlsxUrl: (year, month) => `${API}/tax/f14.xlsx?year=${year}&month=${month}`,
  // ── Exportaciones PDF ──
  libroVentasPdfUrl: (year, month) => `${API}/tax/libro-ventas.pdf?year=${year}&month=${month}`,
  libroComprasPdfUrl: (year, month) => `${API}/tax/libro-compras.pdf?year=${year}&month=${month}`,
  libroDiarioPdfUrl: (year, month) => `${API}/tax/libro-diario.pdf?year=${year}&month=${month}`,
  libroMayorPdfUrl: (codigo, year, month) => {
    const qs = new URLSearchParams({ codigo });
    if (year) qs.set("year", year);
    if (month) qs.set("month", month);
    return `${API}/tax/libro-mayor.pdf?${qs.toString()}`;
  },
  f14PdfUrl: (year, month) => `${API}/tax/f14.pdf?year=${year}&month=${month}`,
  // ── Respaldo mensual ZIP ──
  backupMensualUrl: (year, month, incluirDtes = true) =>
    `${API}/tax/backup-mensual.zip?year=${year}&month=${month}&incluirDtes=${incluirDtes}`,
  // ── CIERRE DE PERÍODO ──
  periodStatus: (year, month) => api.get("/period/status", { params: { year, month } }).then(r => r.data),
  periodClose: (year, month, forzar = false) => api.post("/period/close", { year, month, forzar }).then(r => r.data),
  periodReopen: (year, month, motivo = "") => api.post("/period/reopen", { year, month, motivo }).then(r => r.data),
  periodList: (limit = 24) => api.get("/period/list", { params: { limit } }).then(r => r.data),
  // ── OPERACIÓN ACTIVA ──
  getAmbiente: () => api.get("/config/ambiente").then(r => r.data),
  putAmbiente: (ambiente_activo) => api.put("/config/ambiente", { ambiente_activo }).then(r => r.data),
  autoPruebasCasos: () => api.get("/auto-pruebas/casos").then(r => r.data),
  autoPruebasEjecutar: (payload = {}) => api.post("/auto-pruebas/ejecutar", payload, { timeout: 600000 }).then(r => r.data),
  autoPruebasPrepararContingencia: (payload = {}) => api.post("/auto-pruebas/preparar-contingencia", payload, { timeout: 120000 }).then(r => r.data),
  autoPruebasContingencia: (payload = {}) => api.post("/auto-pruebas/contingencia", payload, { timeout: 600000 }).then(r => r.data),
  autoPruebasInvalidar: (payload = {}) => api.post("/auto-pruebas/invalidar", payload, { timeout: 600000 }).then(r => r.data),
  autoPruebasRetorno: (payload = {}) => api.post("/auto-pruebas/retorno", payload, { timeout: 600000 }).then(r => r.data),
  // ── ALERTAS ──
  alertasList: () => api.get("/alertas").then(r => r.data),
  // ── HERRAMIENTAS DTE FLOW LOCAL ──
  localStatus: () => api.get("/local/status").then(r => r.data),
  localBackupUrl: () => `${API}/local/backup.zip`,
  localDiagnosticsUrl: () => `${API}/local/diagnostics.zip`,
  localRunAutoBackup: (force = false) => api.post("/local/auto-backup", null, { params: { force } }).then(r => r.data),
  localRestore: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post("/local/restore", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 180000,
    }).then(r => r.data);
  },
  localLicense: () => api.get("/local/license").then(r => r.data),
  localActivateLicense: (serial) => api.post("/local/license", { serial }).then(r => r.data),
  localUpdateInfo: () => api.get("/local/update").then(r => r.data),
  localCloudSync: () => api.get("/local/cloud-sync").then(r => r.data),
  localCloudSyncConfig: (payload) => api.post("/local/cloud-sync/config", payload).then(r => r.data),
  localCloudSyncPull: () => api.post("/local/cloud-sync/pull", {}).then(r => r.data),
  localCloudSyncPush: () => api.post("/local/cloud-sync/push", {}).then(r => r.data),
  // ── CLIENTES ──
  clientesList: (params = {}) => api.get("/clientes", { params }).then(r => r.data),
  clientesGet: (id) => api.get(`/clientes/${encodeURIComponent(id)}`).then(r => r.data),
  clientesCreate: (data) => api.post("/clientes", data).then(r => r.data),
  clientesUpdate: (id, data) => api.put(`/clientes/${encodeURIComponent(id)}`, data).then(r => r.data),
  clientesDelete: (id) => api.delete(`/clientes/${encodeURIComponent(id)}`).then(r => r.data),
  // -- INVENTARIO --
  inventoryList: (params = {}) => api.get("/inventory", { params }).then(r => r.data),
  inventoryGet: (id) => api.get(`/inventory/${encodeURIComponent(id)}`).then(r => r.data),
  inventoryCreate: (data) => api.post("/inventory", data).then(r => r.data),
  inventoryUpdate: (id, data) => api.patch(`/inventory/${encodeURIComponent(id)}`, data).then(r => r.data),
  inventoryDelete: (id) => api.delete(`/inventory/${encodeURIComponent(id)}`).then(r => r.data),
  inventoryAdjust: (id, data) => api.post(`/inventory/${encodeURIComponent(id)}/adjust`, data).then(r => r.data),
  inventoryReport: (params = {}) => api.get("/inventory/report", { params }).then(r => r.data),
  inventoryReportXlsxUrl: (params = {}) => {
    const qs = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ""))).toString();
    return `${API}/inventory/report.xlsx${qs ? `?${qs}` : ""}`;
  },
  inventoryReportPdfUrl: (params = {}) => {
    const qs = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ""))).toString();
    return `${API}/inventory/report.pdf${qs ? `?${qs}` : ""}`;
  },
  // ── IMPORTACIONES ──
  importsList: (params = {}) => api.get("/imports", { params }).then(r => r.data),
  importsCreate: (data) => api.post("/imports", data).then(r => r.data),
  importsDelete: (id) => api.delete(`/imports/${id}`).then(r => r.data),
  // ── NOTIFICACIONES (Resend automáticas) ──
  notificationsTestEmail: () => api.post("/notifications/test-email").then(r => r.data),
  notificationsLog: () => api.get("/notifications/log").then(r => r.data),
  // ── MULTI-EMPRESA (companies) ──
  companiesList: () => api.get("/me/companies").then(r => r.data),
  companiesCreate: (data) => api.post("/me/companies/create", data).then(r => r.data),
  companiesSwitch: (tenant_id) => api.post("/me/companies/switch", { tenant_id }).then(r => r.data),
  companiesUpdateAlias: (tenant_id, alias) => api.put("/me/companies/alias", { tenant_id, alias }).then(r => r.data),
  companiesLeave: (tenant_id) => api.post("/me/companies/leave", { tenant_id }).then(r => r.data),
  companiesSetDefault: (tenant_id) => api.post("/me/companies/set-default", { tenant_id }).then(r => r.data),
  companiesMembers: () => api.get("/me/companies/members").then(r => r.data),
  companiesInvite: (data) => api.post("/me/companies/invite", data).then(r => r.data),
  companiesUpdateRole: (user_id, role) => api.put("/me/companies/role", { user_id, role }).then(r => r.data),
  companiesRemoveMember: (user_id) => api.delete(`/me/companies/members/${encodeURIComponent(user_id)}`).then(r => r.data),
  // ── INVITE LINKS (iter39) ──
  inviteLinkCreate: (data) => api.post("/me/companies/invite-link", data).then(r => r.data),
  inviteLinkInfo: (token) => api.get(`/me/companies/invite-link/${encodeURIComponent(token)}`).then(r => r.data),
  inviteLinkAccept: (token) => api.post(`/me/companies/invite-link/${encodeURIComponent(token)}/accept`).then(r => r.data),
  inviteLinkList: () => api.get("/me/companies/invite-links").then(r => r.data),
  inviteLinkRevoke: (token) => api.delete(`/me/companies/invite-link/${encodeURIComponent(token)}`).then(r => r.data),
  // ── F-14 XML ──
  f14XmlUrl: (year, month) => `${API}/tax/f14.xml?year=${year}&month=${month}`,
  // ── F-14 RENTA (Pago a cuenta + Retenciones de Renta) ──
  f14Renta: (year, month, casilla) => api.get("/tax/f14-renta", { params: { year, month, casilla } }).then(r => r.data),
  f14RentaXmlUrl: (year, month) => `${API}/tax/f14-renta.xml?year=${year}&month=${month}`,
  f14RentaXlsxUrl: (year, month) => `${API}/tax/f14-renta.xlsx?year=${year}&month=${month}`,
  f14RentaPdfUrl: (year, month) => `${API}/tax/f14-renta.pdf?year=${year}&month=${month}`,
  f14RentaConfigGet: () => api.get("/tax/f14-renta/config").then(r => r.data),
  f14RentaConfigPut: (casilla) => api.put("/tax/f14-renta/config", { casilla }).then(r => r.data),
  // ── F-07 alias canónico (apunta al mismo F-14 IVA pero con nombre técnicamente correcto) ──
  f07Url: (year, month) => `${API}/tax/f07?year=${year}&month=${month}`,
  f07XmlUrl: (year, month) => `${API}/tax/f07.xml?year=${year}&month=${month}`,
  f07XlsxUrl: (year, month) => `${API}/tax/f07.xlsx?year=${year}&month=${month}`,
  f07PdfUrl: (year, month) => `${API}/tax/f07.pdf?year=${year}&month=${month}`,
  passwordStatus: (ambiente = null) => api.get("/certs/password-status", {
    params: ambiente ? { ambiente } : {},
  }).then(r => {
    const d = r.data || {};
    return d;
  }),
  receptores: () => api.get("/config/receptores").then(r => r.data),
  addReceptor: (rec) => api.post("/config/receptores", rec).then(r => r.data),
  delReceptor: (nit) => api.delete(`/config/receptores/${nit}`).then(r => r.data),
  validateCert: (nit, passwordPri, ambiente) => api.post("/certs/validate", { nit, passwordPri, ambiente }).then(r => r.data),
  checkPassword: (nit, password, ambiente) => api.post("/certs/check-password", { nit, password, ambiente }).then(r => r.data),
  invalidar: (payload) => api.post("/dte/invalidar", payload).then(r => r.data),
  contingencia: (payload) => api.post("/dte/contingencia", payload).then(r => r.data),
  retorno: (payload) => api.post("/dte/eret", payload, { timeout: 600000 }).then(r => r.data),
  firmadorHealth: () => api.get("/firmador/health").then(r => r.data),
  validateDte: (documento, tipoDte) => api.post("/dte/validate",
    { documento, tipoDte }, { timeout: 30000 }).then(r => r.data),
  complianceLetterUrl: () => `${API}/compliance/letter`,
  // iter50: limpieza total / factory reset (admin only)
  wipeData: (payload) => api.post("/admin/wipe-data", payload, { timeout: 120000 }).then(r => r.data),
  // iter51: suscripciones SaaS
  subscription: () => api.get("/subscription").then(r => r.data),
  subscriptionPlans: () => api.get("/subscription/plans").then(r => r.data),
  subscriptionChange: (plan) => api.post("/subscription/change", { plan }).then(r => r.data),
  // iter51c: panel de estado de integraciones (admin only)
  integrationsStatus: () => api.get("/integrations/status", { timeout: 15000 }).then(r => r.data),
  integrationsTestResend: () => api.post("/integrations/test/resend", {}, { timeout: 30000 }).then(r => r.data),
  integrationsTestFirmador: () => api.post("/integrations/test/firmador", {}, { timeout: 15000 }).then(r => r.data),
  // iter64: diagnóstico granular + recuperación manual del firmador (admin only)
  firmadorDiagnose: () => api.get("/integrations/firmador/diagnose", { timeout: 15000 }).then(r => r.data),
  firmadorRestart: () => api.post("/integrations/firmador/restart", {}, { timeout: 60000 }).then(r => r.data),
  // iter64: MH Outbox (dead-letter) — admin only
  outboxList: (estado) => api.get("/mh/outbox", { params: estado ? { estado } : {}, timeout: 15000 }).then(r => r.data),
  outboxRetry: (id) => api.post(`/mh/outbox/${encodeURIComponent(id)}/retry`, {}, { timeout: 60000 }).then(r => r.data),
  outboxDiscard: (id) => api.post(`/mh/outbox/${encodeURIComponent(id)}/discard`, {}, { timeout: 15000 }).then(r => r.data),
  // iter64: Modo emergencia manual — admin only
  manualJsonUnsigned: (entryId) => api.get(`/dte/manual/${encodeURIComponent(entryId)}/json-unsigned`, { timeout: 15000 }).then(r => r.data),
  manualUploadSigned: (entryId, jws) => api.post(`/dte/manual/${encodeURIComponent(entryId)}/upload-signed`, { jws }, { timeout: 30000 }).then(r => r.data),
  manualUploadMhResponse: (entryId, payload) => api.post(`/dte/manual/${encodeURIComponent(entryId)}/upload-mh-response`, payload, { timeout: 15000 }).then(r => r.data),
  // iter58: Pagadito
  paymentsCheckout: (plan, months = 1, coupon_code = undefined) => api.post("/payments/checkout", { plan, months, coupon_code }).then(r => r.data),
  paymentsHealth: () => api.get("/payments/health").then(r => r.data),
  paymentsMyIntents: (limit = 50) => api.get("/payments/intents", { params: { limit } }).then(r => r.data),
  paymentsIntentStatus: (intent_id) => api.get(`/payments/intents/${intent_id}/status`).then(r => r.data),
  // iter61: PDF preliminar (sin firmar, sin MH)
  dtePreliminarPdf: (documento) => api.post("/dte/preliminar", { documento }, { responseType: "blob", timeout: 30000 }).then(r => r.data),
  dtePreliminarEmail: (documento, to, subject = null) => api.post("/dte/preliminar", { documento, send_email: true, to, subject }).then(r => r.data),
  dtePreliminaresList: (limit = 50) => api.get("/dte/preliminares", { params: { limit } }).then(r => r.data),
  // ── iter53: SUPER ADMIN (gestión global de plataforma) ──
  saDashboard: (product = "") => api.get("/superadmin/dashboard", { params: withProduct({}, product) }).then(r => r.data),
  saCommercialOverview: (period = "") => api.get("/superadmin/commercial-overview", { params: period ? { period } : {} }).then(r => r.data),
  saFiscalOverview: (period = "") => api.get("/superadmin/fiscal-overview", { params: period ? { period } : {} }).then(r => r.data),
  saCompanies: (q = "", page = 1, limit = 25, product = "") => api.get("/superadmin/companies", { params: withProduct({ q, page, limit }, product) }).then(r => r.data),
  saCreateCompany: (payload) => api.post("/superadmin/companies", payload).then(r => r.data),
  saDeleteCompany: (tenant_id, payload) => api.post(`/superadmin/companies/${encodeURIComponent(tenant_id)}/delete`, payload).then(r => r.data),
  saUsers: (q = "", limit = 100, product = "") => api.get("/superadmin/users", { params: withProduct({ q, limit }, product) }).then(r => r.data),
  saReengagementPreview: (product = "all", limit = 500) => api.get("/superadmin/reengagement/preview", { params: { product, limit } }).then(r => r.data),
  saSendReengagement: (payload) => api.post("/superadmin/reengagement/send", payload, { timeout: 180000 }).then(r => r.data),
  saCreateUser: (payload) => api.post("/superadmin/users", payload).then(r => r.data),
  saDeleteUser: (user_id, payload) => api.post(`/superadmin/users/${encodeURIComponent(user_id)}/delete`, payload).then(r => r.data),
  saRemoveMembership: (user_id, tenant_id, payload) => api.post(`/superadmin/users/${encodeURIComponent(user_id)}/memberships/${encodeURIComponent(tenant_id)}/remove`, payload).then(r => r.data),
  saCleanupOrphanRecords: (payload) => api.post("/superadmin/data-quality/orphans/cleanup", payload).then(r => r.data),
  saSubscriptions: (status = "", plan = "", limit = 200, product = "") => api.get("/superadmin/subscriptions", { params: withProduct({ status, plan, limit }, product) }).then(r => r.data),
  saChangePlan: (tenant_id, plan, notes = "", product = "") => api.post(`/superadmin/subscriptions/${tenant_id}/change`, withProduct({ plan, notes }, product)).then(r => r.data),
  saActivate: (tenant_id, payload = {}, product = "") => api.post(`/superadmin/subscriptions/${tenant_id}/activate`, withProduct(payload, product)).then(r => r.data),
  saSuspend: (tenant_id, notes = "", product = "") => api.post(`/superadmin/subscriptions/${tenant_id}/suspend`, withProduct({ notes }, product)).then(r => r.data),
  saRenew: (tenant_id, months = 1, product = "") => api.post(`/superadmin/subscriptions/${tenant_id}/renew`, withProduct({ months }, product)).then(r => r.data),
  saRegisterPayment: (tenant_id, payload, product = "") => api.post(`/superadmin/subscriptions/${tenant_id}/payment`, withProduct(payload, product)).then(r => r.data),
  saPayments: (tenant_id = "", limit = 200, product = "") => api.get("/superadmin/payments", { params: withProduct({ tenant_id, limit }, product) }).then(r => r.data),
  saCoupons: (status = "active", limit = 200) => api.get("/superadmin/coupons", { params: { status, limit } }).then(r => r.data),
  saCreateCoupon: (payload) => api.post("/superadmin/coupons", payload).then(r => r.data),
  saActivateCoupon: (code) => api.post(`/superadmin/coupons/${encodeURIComponent(code)}/activate`, {}).then(r => r.data),
  saDeactivateCoupon: (code) => api.post(`/superadmin/coupons/${encodeURIComponent(code)}/deactivate`, {}).then(r => r.data),
  saAudit: (params = {}) => api.get("/superadmin/audit", { params }).then(r => r.data),
  saUpgradeRequests: (status = "pending", limit = 100, product = "") => api.get("/superadmin/upgrade-requests", { params: withProduct({ status, limit }, product) }).then(r => r.data),
  saResolveUpgradeRequest: (req_id, notes = "", product = "", applyPlan = false) => api.post(`/superadmin/upgrade-requests/${req_id}/resolve`, withProduct({ notes, apply_plan: applyPlan }, product)).then(r => r.data),
  // Cliente: solicitar upgrade (auth user, no super admin)
  subscriptionRequestUpgrade: (plan, message = "") => api.post("/subscription/request-upgrade", { plan, message }).then(r => r.data),
};

/**
 * humanizeError(err) — extrae un mensaje string SEGURO para mostrar al usuario.
 * Maneja:
 *   - HTTPException FastAPI con `detail: string`
 *   - HTTPException con `detail: {code, message, feature, planLabel, upgradeOptions}` (402)
 *   - Pydantic ValidationError con `detail: [{msg, loc}, ...]`
 *   - Errores genéricos / network errors
 *
 * NUNCA devuelve un objeto. Devuelve siempre un string.
 */
export function humanizeError(err, fallback = "Ocurrió un error inesperado") {
  const detail = err?.response?.data?.detail;
  const data = err?.response?.data;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    // Pydantic validation errors
    return detail.map(d => d?.msg || JSON.stringify(d)).join("; ");
  }
  if (detail && typeof detail === "object") {
    if (typeof detail.message === "string") return detail.message;
    if (typeof detail.error === "string") return detail.error;
    return JSON.stringify(detail);
  }
  if (data && typeof data === "object") {
    if (typeof data.summary === "string") return data.summary;
    if (typeof data.message === "string") return data.message;
    if (typeof data.error === "string") return data.error;
  }
  if (typeof err?.response?.data?.message === "string") return err.response.data.message;
  if (typeof err?.message === "string") return err.message;
  return fallback;
}

/**
 * extractErrorPayload(err) — devuelve el objeto detail si es un FEATURE_LOCKED u
 * otro error estructurado. Útil para mostrar modales con upgradeOptions.
 */
export function extractErrorPayload(err) {
  const d = err?.response?.data?.detail;
  return (d && typeof d === "object" && !Array.isArray(d)) ? d : null;
}

export default api;
